import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Clock, LogOut, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Show warning dialog 30 seconds before session expires
const WARNING_BEFORE_EXPIRY_SECONDS = 30;

// Time to respond to the dialog before auto-logout (in seconds)
const DIALOG_TIMEOUT_SECONDS = 25;

const SessionManager = ({ children }) => {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(DIALOG_TIMEOUT_SECONDS);
  const [sessionTimeout, setSessionTimeout] = useState(180);
  const checkIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const hasShownWarningRef = useRef(false);

  const getSessionToken = useCallback(() => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'session_token') {
        return value;
      }
    }
    return null;
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear cookie
    document.cookie = 'session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; samesite=none';
    
    // Clear intervals
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    setShowWarning(false);
    navigate('/login-local', { replace: true });
  }, [navigate]);

  const handleRefreshSession = useCallback(async () => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/refresh`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        // Update cookie with new expiration
        const token = getSessionToken();
        if (token) {
          document.cookie = `session_token=${token}; path=/; max-age=${response.data.timeout_seconds}; secure; samesite=none`;
        }
        
        setShowWarning(false);
        hasShownWarningRef.current = false;
        setCountdown(DIALOG_TIMEOUT_SECONDS);
        
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
        
        console.log('Session refreshed successfully');
      }
    } catch (error) {
      console.error('Failed to refresh session:', error);
      handleLogout();
    }
  }, [getSessionToken, handleLogout]);

  const checkSession = useCallback(async () => {
    const token = getSessionToken();
    
    if (!token) {
      // No session, don't do anything (user might be on login page)
      return;
    }

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/auth/session-info`,
        { withCredentials: true }
      );
      
      const { time_remaining_seconds, timeout_seconds, is_expired } = response.data;
      
      setSessionTimeout(timeout_seconds);
      
      if (is_expired) {
        handleLogout();
        return;
      }
      
      // Show warning dialog when approaching expiry
      if (time_remaining_seconds <= WARNING_BEFORE_EXPIRY_SECONDS && !hasShownWarningRef.current) {
        hasShownWarningRef.current = true;
        setCountdown(Math.min(time_remaining_seconds, DIALOG_TIMEOUT_SECONDS));
        setShowWarning(true);
        
        // Start countdown
        countdownIntervalRef.current = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef.current);
              handleLogout();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      // If 401, session expired
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  }, [getSessionToken, handleLogout]);

  useEffect(() => {
    // Check session every 5 seconds
    checkIntervalRef.current = setInterval(checkSession, 5000);
    
    // Initial check
    checkSession();
    
    return () => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [checkSession]);

  const handleCancel = () => {
    setShowWarning(false);
    handleLogout();
  };

  const handleConfirm = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    handleRefreshSession();
  };

  return (
    <>
      {children}
      
      <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <Clock className="h-5 w-5" />
              Sessão a Expirar
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  A sua sessão irá expirar em breve por motivos de segurança.
                </p>
                
                <div className="flex items-center justify-center">
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-full w-20 h-20 flex items-center justify-center">
                    <span className="text-3xl font-bold text-amber-600">{countdown}</span>
                  </div>
                </div>
                
                <p className="text-center text-sm text-slate-500">
                  segundos restantes
                </p>
                
                <div className="bg-slate-50 p-3 rounded-lg text-sm">
                  <p className="font-medium text-slate-700">Deseja continuar a trabalhar?</p>
                  <p className="text-slate-500 mt-1">
                    Clique em &quot;Continuar Sessão&quot; para prolongar por mais {sessionTimeout / 60} minutos.
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel 
              onClick={handleCancel}
              className="flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Terminar Sessão
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirm}
              className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Continuar Sessão
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SessionManager;
